/**
 * Riding out an API restart without an error in the chat (#813 item 1).
 *
 * A deployment restarts the two API instances one after the other. A chat whose
 * live stream runs through the restarting instance has that stream CUT, while the
 * worker carries on with the turn. That is a lost connection, not a failed turn,
 * so the client:
 *  - re-attaches to the same run (the server's prompt-less POST relays the live
 *    run), retrying with backoff while the instance comes back (`recoverRun`);
 *  - retries a send that hit a restarting instance (502/503/504 or a network
 *    error) with the SAME runId (`fetchWithGatewayRetry`). If the first attempt had
 *    in fact been admitted, the retry is refused SESSION_BUSY, and the caller
 *    re-attaches to that run instead of showing an error.
 */

export interface RetryOpts {
  sleep?: (ms: number) => Promise<void>
  maxAttempts?: number
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))
/** Backoff: about 20s in total, longer than one API instance's restart (~9-10s). */
const delayFor = (attempt: number) => Math.min(500 * 2 ** attempt, 4000)

/** A response or error that means "the instance is restarting / unreachable",
 *  never a real answer from the application. An abort is never one. */
export function isGatewayFailure(x: unknown): boolean {
  if (x instanceof DOMException && x.name === 'AbortError') return false
  if (x instanceof TypeError) return true // fetch's network failure
  const status = (x as { status?: unknown } | null)?.status
  // 502/503/504 from nginx; 520-524 from Cloudflare in front of the API origin.
  return typeof status === 'number' && (status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 524))
}

/** Run `doFetch`, retrying while it hits a gateway failure. Returns the first real
 *  answer (or the last gateway response when the budget runs out) and whether a
 *  retry happened. Rethrows an abort or, after the budget, the last network error. */
export async function fetchWithGatewayRetry(
  doFetch: () => Promise<Response>,
  opts: RetryOpts = {},
): Promise<{ res: Response; retried: boolean }> {
  const sleep = opts.sleep ?? defaultSleep
  const max = opts.maxAttempts ?? 7
  let lastErr: unknown
  let lastRes: Response | undefined
  for (let attempt = 0; attempt < max; attempt++) {
    if (attempt > 0) await sleep(delayFor(attempt - 1))
    try {
      const res = await doFetch()
      if (!isGatewayFailure(res)) return { res, retried: attempt > 0 }
      lastRes = res
    } catch (err) {
      if (!isGatewayFailure(err)) throw err
      lastErr = err
    }
  }
  if (lastRes) return { res: lastRes, retried: true }
  throw lastErr
}

/** One re-attach attempt's outcome: the run was streamed to its end, the session has
 *  no run any more (it finished during the cut), try again (network/5xx/cut), or
 *  another run now owns the session (a resend / resume / another surface) — stop,
 *  never claim over it. */
export type AttachOutcome = 'streamed' | 'not-running' | 'retry' | 'superseded' | 'cut-after-progress' | 'reconnect' | 'reconnect-after-progress'

/** #840 final review B-I3: how an attach that ended without a terminal frame ended. A
 *  stream that made PROGRESS — a real event beyond the attach bootstrap, or held open
 *  10s (a parked turn only pings) — is a healthy run the server cut, not a storm, so a
 *  server-requested reconnect on it resets the counters like 'cut-after-progress' does.
 *  `openForMs` is null when the stream never opened. */
export function cutOutcomeFor(s: { reconnectAsked: boolean; progressed: boolean; openForMs: number | null }): AttachOutcome {
  const madeProgress = s.progressed || (s.openForMs !== null && s.openForMs >= 10_000)
  if (s.reconnectAsked) return madeProgress ? 'reconnect-after-progress' : 'reconnect'
  return madeProgress ? 'cut-after-progress' : 'retry'
}

/** #840: consecutive server-requested reconnects retried with NO backoff. Past this
 *  (the new instance is shutting down too), fall back to the cut-after-progress path. */
export const MAX_IMMEDIATE_RECONNECTS = 3

/** An attach outcome that means "the run may still be going — re-attach". */
export function isRecoverableOutcome(o: AttachOutcome): boolean {
  return o === 'retry' || o === 'cut-after-progress' || o === 'reconnect' || o === 'reconnect-after-progress'
}

/** A re-attach POST's HTTP answer: only a definite "nothing to attach to" (400) or a
 *  definite refusal ends recovery; any 5xx (a draining instance, a Redis blip, a CF
 *  52x), 408 and 429 are retried — ending there would flip a live turn to idle. */
export function attachOutcomeForStatus(status: number): 'not-running' | 'retry' {
  return status >= 500 || status === 408 || status === 429 ? 'retry' : 'not-running'
}

/** Re-attach to a run whose stream was cut, until it streams to the end, turns out to
 *  be finished, another run supersedes it, `shouldStop` says the user moved on
 *  (cancelled / unmounted) — checked AFTER each backoff, right before the attempt —
 *  or the budget runs out ('gave-up'). The budget counts CONSECUTIVE failures to get
 *  back: an attempt that relayed the run before being cut again
 *  ('cut-after-progress' — the other instance restarting, a second deploy) starts
 *  the count and the backoff over, so a long turn can cross any number of restarts; a
 *  server-requested reconnect on a progressed stream ('reconnect-after-progress') resets
 *  it too, without the backoff. */
export async function recoverRun(
  attempt: () => Promise<AttachOutcome>,
  opts: RetryOpts & { shouldStop?: () => boolean } = {},
): Promise<'streamed' | 'not-running' | 'stopped' | 'gave-up'> {
  const sleep = opts.sleep ?? defaultSleep
  const max = opts.maxAttempts ?? 8
  let reconnects = 0
  // An immediate reconnect skips ONLY the next backoff sleep; it never refills the
  // failure budget (a mixed retry/reconnect storm must still end in 'gave-up').
  let skipSleep = false
  for (let failures = 0; failures < max;) {
    if (failures > 0 && !skipSleep) await sleep(delayFor(failures - 1))
    skipSleep = false
    if (opts.shouldStop?.()) return 'stopped'
    const r = await attempt()
    if (r === 'superseded') return 'stopped'
    if (r === 'reconnect') {
      // Up to MAX_IMMEDIATE_RECONNECTS cost nothing — re-attach at once, keeping the
      // failure count. Past that (a crash-looping deploy, a proxy only reaching
      // draining instances) each further reconnect is a FAILURE toward the normal
      // budget, cumulatively — a sustained storm still ends in 'gave-up'.
      // `reconnects` resets only on real progress ('cut-after-progress'), never on a
      // plain 'retry' — else retry/reconnect alternation would refill it forever.
      if (reconnects < MAX_IMMEDIATE_RECONNECTS) { reconnects++; skipSleep = true; continue }
      failures++
      continue
    }
    if (r === 'cut-after-progress') { reconnects = 0; failures = 1; continue }
    // B-I3: a reconnect the server asked for on a stream that PROGRESSED (every rolling
    // deploy sends one or two to a long turn) resets both counters like a cut after
    // progress, and re-attaches at once — it counts as the first immediate reconnect of
    // a fresh burst, so a storm that follows still ends in 'gave-up'.
    if (r === 'reconnect-after-progress') { reconnects = 1; failures = 0; continue }
    if (r !== 'retry') return r
    failures++
  }
  return 'gave-up'
}

/**
 * #762 — after `recoverRun` gives up, keep checking QUIETLY instead of leaving the chat stuck on "Lost the connection
 * … reload the page". The connection usually comes back (a QUIC drop, a gateway hiccup) while the turn is still running
 * or has just finished — and nothing used to look again until a reload. This asks the server's busy set
 * (`/sessions/status`) until it gets an answer: still running → re-attach; not running → the turn ended, show it.
 * An unreachable server is not an answer — it keeps waiting, backing off to `maxDelayMs`, for at most `giveUpAfterMs`
 * (then 'timed-out': the caller shows the old error). Past that deadline even a "running" answer is 'timed-out': the
 * caller carries the deadline across give-up → re-attach cycles that make no progress, so a stream that keeps failing
 * without delivering anything can't cycle forever (a re-attach that streams starts a fresh episode — intended). `wake()` cuts the current wait short (the browser came back online, the tab
 * was shown again). Stops at once when `shouldStop()` (a Cancel, an unmount, a new run took the session over).
 */
export interface ReconcileOpts {
  /** The busy set: the sessions whose turn is still running. Throws when the server can't be reached. */
  fetchBusy: () => Promise<Array<{ id: string }>>
  shouldStop?: () => boolean
  sleep?: (ms: number, wake: Promise<void>) => Promise<void>
  now?: () => number
  /** Resolves the current wait early; re-armed for each wait. */
  wakeSignal?: () => Promise<void>
  firstDelayMs?: number
  maxDelayMs?: number
  giveUpAfterMs?: number
}

export const RECONCILE_GIVE_UP_AFTER_MS = 30 * 60_000

export async function reconcileAfterGiveUp(
  sessionId: string,
  opts: ReconcileOpts,
): Promise<'running' | 'finished' | 'stopped' | 'timed-out'> {
  const now = opts.now ?? Date.now
  const sleep = opts.sleep ?? ((ms: number, wake: Promise<void>) => Promise.race([defaultSleep(ms), wake]))
  const deadline = now() + (opts.giveUpAfterMs ?? RECONCILE_GIVE_UP_AFTER_MS)
  let delay = opts.firstDelayMs ?? 3000
  const maxDelay = opts.maxDelayMs ?? 15_000
  for (;;) {
    await sleep(delay, opts.wakeSignal?.() ?? new Promise<void>(() => {}))
    if (opts.shouldStop?.()) return 'stopped'
    try {
      const busy = await opts.fetchBusy()
      if (opts.shouldStop?.()) return 'stopped'
      if (!busy.some((s) => s.id === sessionId)) return 'finished'
      // Still running — but past the deadline (an episode of repeated drops, carried by the caller across cycles), stop
      // re-attaching and let the caller say so: the connection keeps failing even though the server is there.
      return now() >= deadline ? 'timed-out' : 'running'
    } catch {
      // #1316: a failure the caller stops on (its session ended — the read was dropped) ends it now: no further wait,
      // no further read
      if (opts.shouldStop?.()) return 'stopped'
      // still unreachable — wait longer, up to the deadline
    }
    if (now() >= deadline) return 'timed-out'
    delay = Math.min(delay * 2, maxDelay)
  }
}

/**
 * #762 — wake `reconcileAfterGiveUp` early when the connection is likely back: the browser fires `online`, or the
 * tab is shown again (a laptop woken from sleep). `next()` gives a promise for the NEXT such moment; `dispose()`
 * removes the listeners. Inert where there is no `window` (tests, a worker).
 */
export function onceBackOnline(): { next: () => Promise<void>; dispose: () => void } {
  if (typeof window === 'undefined') return { next: () => new Promise<void>(() => {}), dispose: () => {} }
  let resolve: (() => void) | null = null
  const fire = () => { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { resolve?.(); resolve = null } }
  window.addEventListener('online', fire)
  document.addEventListener('visibilitychange', fire)
  return {
    next: () => new Promise<void>((r) => { resolve = r }),
    dispose: () => { window.removeEventListener('online', fire); document.removeEventListener('visibilitychange', fire); resolve = null },
  }
}
